import { Icon } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import classNames from "classnames";
import moment from "moment";
import { CaptionBody, SmallBodyBolder } from "src/components/Text";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { FileCardProps } from "./FileCard.types";

/** "Kassarutiner.pdf" -> "pdf"; undefined when the name has no extension. */
export const getFileExtension = (name?: string): string | undefined => {
    const match = /\.([a-z0-9]{1,8})$/i.exec(name || "");

    return match ? match[1].toLowerCase() : undefined;
};

/**
 * A single file: type icon, name, "PDF · Uppdaterad {datum} · {storlek}" and "Ladda ner".
 */
function FileCard({
    name,
    downloadHref,
    viewHref,
    extension,
    updated,
    size,
    openInSameWindow,
    className,
}: FileCardProps): JSX.Element {
    const target = openInSameWindow ? "_self" : "_blank";
    const meta = [
        extension ? extension.toUpperCase() : undefined,
        updated ? localize("UppdateradDatum").replace("{0}", moment(updated).format("LL")) : undefined,
        size,
    ].filter(Boolean).join(" · ");

    return (
        <div className={classNames("FileCard", className)}>
            <div className="FileCard-icon" aria-hidden="true">
                <Icon {...getFileTypeIconProps({ extension: extension || "", size: 20 })} />
            </div>
            <div className="FileCard-text">
                <a className="FileCard-name" href={viewHref || downloadHref} target={target} rel="noreferrer" title={name}>
                    <SmallBodyBolder as="span" weight="semiBold" color="contentDark">
                        {name}
                    </SmallBodyBolder>
                </a>
                {!!meta && (
                    <CaptionBody as="div" color="contentNormal" className="FileCard-meta">
                        {meta}
                    </CaptionBody>
                )}
            </div>
            <a
                className="FileCard-download"
                href={downloadHref}
                target={target}
                rel="noreferrer"
                aria-label={localize("LaddaNer") + " " + name}>
                <Visage2Icon className="FileCard-downloadIcon" icon="document-download" size="small" />
                <SmallBodyBolder as="span" className="FileCard-downloadText">
                    {localize("LaddaNer")}
                </SmallBodyBolder>
            </a>
        </div>
    );
}

export default FileCard;
